import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../../Context/AppContext';
import { useAppDispatch, useAppSelector } from '../../Hooks/Hooks';
import { RootState } from '../../Redux/Store';
import {
  handleResetSearchQuery,
  handleResetSearchQueryError,
  updateSearchQuery,
} from '../../Redux/Reducers/SearchSlice';
import { EVENT, LEAD, SCREEN } from '../../Constants/AnalyticsConstants';
import { fbLogEvent } from '../../Helpers/FirebaseHelper';
import { isEntitled } from '../../Utils/ContentOrConfigUtil';
import { commonDateFormat } from '../../Utils/DateUtils';
import { navBarTabs } from '../../Utils/Navbar.utils';
import { createNatNalSearchPayload } from '../../Utils/App.utils';
import { KEYS, TOOLTIP_MESSAGE } from '../../Constants/Constants';
import ToolTip from '../Shared/Tooltip/Tooltip';
import './Navbar.scss';

interface Tab {
  label: string;
  path: string;
  slug: string;
  icon: string;
  visibilityKey: string;
  isMobile?: boolean;
  subCategory?: Tab[];
}

const Navbar = () => {
  const tabs = navBarTabs();
  let { pathname } = useLocation();
  const { isMobile } = useContext(AppContext);
  const [activeTooltip, setActiveTooltip] = useState('');
  const [selectedTab, setSelectedTab] = useState('');
  const [showMoreBar, setShowMoreBar] = useState(false);
  const { lastLocation, nextAvailableLocation, nextAvailableTime } = useAppSelector(
    (state: RootState) => state.NatNal,
  );
  const { memberData } = useAppSelector((state: RootState) => state.member);
  const { configTableData } = useAppSelector((state: RootState) => state.ContentOrConfig);
  const dispatch = useAppDispatch();
  const appConfigData = configTableData?.FPO_APP_CONFIG;

  useEffect(() => {
    const currentTab =
      tabs?.find((item: any) => item?.path === pathname) ||
      subcategory?.find((item: any) => item?.path === pathname);
    if (currentTab && currentTab?.slug !== selectedTab) {
      return setSelectedTab(currentTab?.slug);
    }
  }, [pathname, selectedTab, tabs]);

  const handleBarClose = () => {
    setShowMoreBar((prev) => !prev);
  };

  const monitorClickEvent = (e: any) => {
    if (
      e.target.closest('.Navbar_Tab_Container') === null &&
      e.target.closest('.Sidebar_Web') === null
    ) {
      setShowMoreBar(false);
      document.removeEventListener('click', monitorClickEvent);
    }
  };

  useEffect(() => {
    if (showMoreBar === true) {
      document.addEventListener('click', monitorClickEvent);
    }
    return () => {
      document.removeEventListener('click', monitorClickEvent);
    };
  }, [showMoreBar]);

  const navigate = useNavigate();
  const logFbEvent = () => {
    fbLogEvent(EVENT.UE_NAT_NAL, {
      screen: SCREEN.HOME,
      lead: SCREEN.LEFT_MENU,
    });
    fbLogEvent(EVENT.SV_VIEW_NAT_NAL, {
      screen: SCREEN.NAT_NAL,
      lead: SCREEN.LEFT_MENU,
      lastLoadId: lastLocation?.shipmentId,
      nextAvailableTime: commonDateFormat(
        nextAvailableTime,
        false,
        false,
        nextAvailableLocation?.timezone || null,
      ),
      nextAvailableLocation: JSON.stringify({
        locationName: nextAvailableLocation?.locationName,
        addressLine1: nextAvailableLocation?.addressLine1,
        addressLine2: nextAvailableLocation?.addressLine2,
        city: nextAvailableLocation?.city,
        state: nextAvailableLocation?.state,
        postalCode: nextAvailableLocation?.postalCode,
      }),
    });
  };

  const onMenuSelect = (tab: Tab) => {
    if (tab?.path === '/') {
      fbLogEvent(EVENT.SV_HOME, {
        screen: SCREEN.HOME,
        lead: isMobile ? LEAD.TAB_BAR : LEAD.LEFT_NAV,
      });
    }
    if (
      tab?.path &&
      (tab?.slug === 'home' ||
        tab?.slug === 'search' ||
        tab?.slug === 'myLoads' ||
        tab?.slug === 'natNal' ||
        tab?.slug === 'financialInfo' ||
        tab?.slug === 'horizon' ||
        tab?.slug === 'feedback' ||
        tab?.slug === 'support' ||
        tab?.slug === 'preferences' ||
        (isMobile && tab?.slug === 'more-mobile') ||
        (isMobile && tab?.slug === 'manage'))
    ) {
      setShowMoreBar(false);
      setActiveTooltip('');
      if (tab?.slug === 'feedback') {
        fbLogEvent(EVENT.UE_FEEDBACK, {
          screen: SCREEN.MORE_MENU,
          lead: SCREEN.MORE_MENU,
        });
      }
      if (tab?.slug === 'support') {
        fbLogEvent(EVENT.UE_SUPPORT, {
          screen: SCREEN.SUPPORT,
          lead: SCREEN.MORE_MENU,
        });
      }
      if (tab?.slug === 'natNal') {
        logFbEvent();
      }

      if (tab?.slug === 'financialInfo') {
        fbLogEvent(EVENT.UE_FINANCIAL_INFO, {
          screen: SCREEN.FINANCIAL_INFO,
          lead: LEAD.LEFT_NAV,
        });
      }

      if (tab?.slug === 'horizon') {
        fbLogEvent(EVENT.UE_HORIZON_NAVIGATION, {
          screen: SCREEN.HORIZON,
          lead: LEAD.LEFT_NAV,
        });
        window?.open(KEYS?.HORIZON_UI_LINK, '_blank');
        return;
      }
      if (tab?.slug === 'search') {
        if (nextAvailableLocation && nextAvailableTime) {
          const payload = createNatNalSearchPayload(
            nextAvailableLocation,
            nextAvailableTime,
            appConfigData,
          );
          dispatch(updateSearchQuery(payload));
        } else {
          dispatch(handleResetSearchQuery());
        }
        dispatch(handleResetSearchQueryError());
      }

      if (tab?.slug === 'preferences') {
        fbLogEvent(EVENT.UE_PREFERENCES, {
          screen: SCREEN.LEFT_NAV_ITEM,
          lead: LEAD.LEFT_NAV,
        });
      }

      return navigate(tab?.path);
    }
    if (tab?.path === '/more') {
      return handleBarClose();
    }
    return setActiveTooltip(tab?.label);
  };
  const subcategory = tabs?.find((tab: Tab) => tab?.path === '/more')?.subCategory;
  return (
    <div className="Navbar">
      {tabs?.map((tab) => {
        if (
          (isMobile && tab?.isMobile === false) ||
          (!isMobile && tab?.path === '/more-mobile') ||
          (!isMobile && tab?.path === '/manage')
        ) {
          return null;
        }
        if (
          !isEntitled(
            memberData?.member,
            configTableData?.FPO_APP_CONFIG?.APP_INIT,
            'tab',
            tab?.slug === 'more-mobile' ? 'more' : tab?.slug,
          )
        ) {
          return null;
        }
        return (
          <div
            key={tab?.slug}
            className={`Navbar_Tab_Container ${
              ((tab?.slug === 'more' || tab?.slug === 'more-mobile') &&
                (selectedTab === 'more-mobile' ||
                  selectedTab === 'feedback' ||
                  selectedTab === 'preferences' ||
                  selectedTab === 'support')) ||
              selectedTab === tab?.slug
                ? 'active'
                : ''
            }`}
          >
            <div className="Tooltip_Container">
              <ToolTip
                message={TOOLTIP_MESSAGE}
                isActive={tab?.label === activeTooltip}
                clearIndex={true}
              />
            </div>
            <div
              className="Navbar_Tab"
              style={{ position: 'relative' }}
              onClick={() => {
                onMenuSelect(tab);
              }}
            >
              <img
                src={
                  tab?.slug === 'search' && selectedTab === 'search' && !isMobile
                    ? `/oo/assets/icon/nav-search-selected.svg`
                    : ((tab?.slug === 'more' || tab?.slug === 'more-mobile') &&
                          (selectedTab === 'more-mobile' ||
                            selectedTab === 'feedback' ||
                            selectedTab === 'preferences' ||
                            selectedTab === 'support')) ||
                        selectedTab === tab?.slug
                      ? `/oo/assets/icon/${tab?.icon_select}`
                      : tab?.slug === 'search' && !isMobile
                        ? `/oo/assets/icon/search_icon_desktop.svg`
                        : `/oo/assets/icon/${tab?.icon}`
                }
                alt={tab?.slug}
              />
              <div className="Navbar_Tab_Label">{tab?.label}</div>
              {showMoreBar && tab?.label === 'More' && (
                <div
                  className="Sidebar_Web"
                  onClick={() => {
                    handleBarClose();
                  }}
                >
                  {subcategory?.map((subTab) => {
                    if (
                      isEntitled(
                        memberData?.member,
                        configTableData?.FPO_APP_CONFIG?.APP_INIT,
                        'tab',
                        subTab?.slug,
                      )
                    )
                      return (
                        <div key={subTab?.slug}>
                          <div
                            className="Tooltip_Container"
                            key={`${subTab?.path} + ${subTab.label}`}
                          >
                            <ToolTip
                              message={TOOLTIP_MESSAGE}
                              isActive={subTab?.slug === activeTooltip}
                              isSidebar={true}
                              clearIndex={true}
                            />
                          </div>
                          <div
                            key={subTab?.slug}
                            onClick={() => {
                              subTab?.path ? onMenuSelect(subTab) : setActiveTooltip(subTab?.label);
                            }}
                          >
                            <img
                              src={
                                selectedTab === subTab?.slug
                                  ? `/oo/assets/icon/${subTab?.icon_select}`
                                  : `/oo/assets/icon/${subTab?.icon}`
                              }
                              alt={subTab?.label}
                            />
                            <div className="Navbar_Tab_Label">{subTab?.label}</div>
                          </div>
                        </div>
                      );
                    return null;
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Navbar;
